import React, { useState } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';
import { addDoc, collection } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import type { Share } from '../types';
import { useTranslation } from '../i18n/useTranslation';

interface ShareDialogProps {
  gameIds: string[];
  onClose: () => void;
  isOpen: boolean;
}

export default function ShareDialog({ gameIds, onClose, isOpen }: ShareDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleShare = async () => {
    setLoading(true);
    setError('');
    
    try {
      const shareData: Omit<Share, 'id'> = {
        createdBy: auth.currentUser?.uid || '',
        gameIds,
        createdAt: new Date()
      };

      const shareDoc = await addDoc(collection(db, 'shares'), shareData);
      const shareUrl = `${window.location.origin}/shared/${shareDoc.id}`;
      setShareUrl(shareUrl);
      
      // Try to use the Share API first if available (better for mobile)
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'GoalR Analysis',
            text: 'Check out this goalie analysis',
            url: shareUrl
          });
          setCopied(true);
        } catch (shareErr) {
          // If share fails, fall back to clipboard
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
        }
      } else {
        // Fallback to clipboard if Share API not available
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
      }
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error creating share:', err);
      setError(t('shareDialog.failedToCreateShare'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError(t('shareDialog.failedToCopyLink'));
    }
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
      <div className='bg-white rounded-lg max-w-md w-full p-6'>
        <div className='flex justify-between items-center mb-6'>
          <h2 className='text-xl font-bold flex items-center'>
            <Share2 className='h-5 w-5 mr-2' />
            {t('shareDialog.title')}
          </h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-500'
          >
            <X className='h-6 w-6' />
          </button>
        </div>

        <p className='text-gray-600 mb-6'>
          {t('shareDialog.description')}
        </p>

        {error && (
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
            {error}
          </div>
        )}

        {shareUrl ? (
          <div className='space-y-4'>
            <div className='flex items-center gap-2 bg-gray-50 p-3 rounded-lg break-all'>
              <span className='text-gray-600 text-sm flex-1'>{shareUrl}</span>
              <button
                onClick={handleCopyLink}
                className='text-[#1b3968] hover:text-[#1b3968]/80 p-1'
              >
                {copied ? (
                  <Check className='h-5 w-5' />
                ) : (
                  <Copy className='h-5 w-5' />
                )}
              </button>
            </div>
            <button
              onClick={onClose}
              className='w-full bg-[#1b3968] text-white px-4 py-3 rounded-md hover:bg-[#1b3968]/90 flex items-center justify-center gap-2'
            >
              {t('shareDialog.done')}
            </button>
          </div>
        ) : (
          <button
            onClick={handleShare}
            disabled={loading}
            className='w-full bg-[#1b3968] text-white px-4 py-3 rounded-md hover:bg-[#1b3968]/90 flex items-center justify-center gap-2 disabled:opacity-50'
          >
            {loading ? (
              <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white'></div>
            ) : (
              <>
                <Share2 className='h-5 w-5' />
                {t('shareDialog.generateShareLink')}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}